// src/components/GlobalFooterStrobe.js
import { useState } from "react";
import { Link } from "react-router-dom";
import { Mail } from "lucide-react";
import { trackClickEvent } from "../utils/analytics";
import strobeLogo from "../assets/images/strobemonochrome.svg";

export default function GlobalFooterStrobe() {
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    trackClickEvent("Footer: Form Submit");
    const form = e.target;
    const formData = new FormData(form);
    fetch(form.action, {
      method: form.method,
      body: formData,
      headers: { Accept: "application/json" },
    })
      .then((res) => {
        if (res.ok) {
          setSubmitted(true);
          form.reset();
        }
      })
      .catch((err) => console.error("Form error:", err));
  };

  return (
    <footer className="bg-strobe-black text-white font-body">
      {/* ── Upper section with gradient overlay ── */}
      <div className="relative">
        <div className="absolute inset-0 bg-gradient-to-br from-black/80 via-black/60 to-black/30 pointer-events-none" />
        <div className="relative z-10 px-8 px-6 md:px-8 pt-8 pb-8">
          <div className="max-w-6xl mx-auto flex flex-col md:flex-row md:space-x-20">
            {/* Newsletter */}
            <div className="md:w-1/2 md:pr-10 md:border-r md:border-white/10 md:mr-12 mb-8 md:mb-0">
              <p className="uppercase text-sm text-white/70 mb-4 tracking-widest font-medium">
                Let’s Do Business
              </p>
              <form
                action="https://formspree.io/f/mqapayyv"
                method="POST"
                className="relative"
                onSubmit={handleSubmit}
              >
                <input type="hidden" name="_subject" value="New Signup from Strobe" />
                <input type="hidden" name="_source" value="FooterSignup" />
                <input
                  type="email"
                  name="email"
                  placeholder="enter your work email"
                  required
                  className="bg-transparent border-b border-strobe-gray text-strobe-gray placeholder:text-strobe-gray focus:outline-none w-full py-2 pr-10 text-lg font-semibold"
                />
                <button
                  type="submit"
                  className="absolute right-0 top-1 text-strobe-gray"
                  onClick={() => trackClickEvent("Footer: Click Mail Icon")}
                >
                  <Mail className="w-5 h-5" />
                </button>
                {submitted && (
                  <p className="text-strobe-red text-sm mt-2">
                    Thanks, you’re subscribed!
                  </p>
                )}
              </form>
            </div>

            {/* Navigation Columns */}
            {/* MOBILE: 3 across | MD: back to 2 | LG: 3 again */}
            <div className="flex-grow grid grid-cols-3 md:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-8">
              {/* FOLLOW */}
              <div>
                <p className="font-heading font-semibold tracking-wider text-lg mb-1">
                  FOLLOW
                </p>
                <ul className="text-sm space-y-1">
                  <li>
                    <a
                      href="https://www.linkedin.com/company/strobeaviation"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-white/70 hover:text-strobe-red transition-colors"
                      onClick={() => trackClickEvent("Footer: LinkedIn")}
                    >
                      LinkedIn
                    </a>
                  </li>
                </ul>
              </div>

              {/* NAVIGATE */}
              <div>
                <p className="font-heading font-semibold tracking-wider text-lg mb-1">
                  NAVIGATE
                </p>
                <ul className="text-sm space-y-2">
                  {[
                    ["About", "/about"],
                    ["Services", "/services"],
                    ["Media", "/media"],
                    ["Blog", "/blog"],
                  ].map(([label, to]) => (
                    <li key={label}>
                      <Link
                        to={to}
                        className="text-white/70 hover:text-strobe-red transition-colors"
                        onClick={() => trackClickEvent(`Footer: ${label}`)}
                      >
                        {label}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>

              {/* CONNECT */}
              <div>
                <p className="font-heading font-semibold tracking-wider text-lg mb-1">
                  CONNECT
                </p>
                <ul className="text-sm space-y-2">
                  <li>
                    <Link
                      to="/contact"
                      className="text-white/70 hover:text-strobe-red transition-colors"
                      onClick={() => trackClickEvent("Footer: Contact")}
                    >
                      Contact
                    </Link>
                  </li>
                  <li>
                    <a
                      href="tel:+18444727422"
                      className="text-white/70 hover:text-strobe-red transition-colors"
                      onClick={() => trackClickEvent("Footer: Phone")}
                    >
                      (844) 4‑STROBE
                    </a>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ── Full‑width bottom bar (solid) ── */}
      <div className="w-full bg-[#111111] border-t border-white/10 py-3">
        <div className="flex flex-row justify-between items-center px-6 md:px-8">
          <p className="text-xs text-white">
            © {new Date().getFullYear()}{" "}
            <span
              className="text-strobe-red cursor-pointer"
              onClick={() => trackClickEvent("Footer: Click Brand Name")}
            >
              Strobe Aviation
            </span>
            . All rights reserved.
          </p>
          <img
            src={strobeLogo}
            alt="Strobe Logo"
            className="w-6 h-6 sm:w-8 sm:h-8 md:w-8 md:h-8 cursor-pointer"
            onClick={() => trackClickEvent("Footer: Click Logo")}
          />
        </div>
      </div>
    </footer>
  );
}
